import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";
import { uploadFileToPresignedUrl } from "@/shared/lib/upload-to-presigned";
import { Button } from "@devhop/ui";
import { Loader2, Upload } from "lucide-react";
import { useCallback, useId, useState } from "react";

type RoomDisplayImageUploadProps = {
  screenId: string;
  onSuccess?: (key: string) => void;
  onError?: (error: Error) => void;
  className?: string;
};

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9._-]/g, "_").slice(0, 100) || "file";
}

export function RoomDisplayImageUpload({
  screenId,
  onSuccess,
  onError,
  className,
}: RoomDisplayImageUploadProps) {
  const inputId = useId();
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setError(null);
      setUploading(true);
      try {
        const { uploadUrl, key } = await fetcher.post<{
          uploadUrl: string;
          key: string;
        }>(`${config.apiUrl}/room-displays/${screenId}/posters/presign`, {
          filename: sanitizeFilename(file.name),
          contentType: file.type,
          sizeBytes: file.size,
        });
        await uploadFileToPresignedUrl(uploadUrl, file, {
          contentType: file.type || undefined,
        });
        onSuccess?.(key);
      } catch (e) {
        const err = e instanceof Error ? e : new Error(String(e));
        setError(err.message);
        onError?.(err);
      } finally {
        setUploading(false);
      }
    },
    [onError, onSuccess, screenId],
  );

  const onInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) void handleFile(file);
      e.target.value = "";
    },
    [handleFile],
  );

  return (
    <div className={`space-y-2 ${className ?? ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          onChange={onInputChange}
          disabled={uploading}
          className="hidden"
          id={inputId}
        />
        <Button
          type="button"
          variant="outline"
          disabled={uploading}
          onClick={() => document.getElementById(inputId)?.click()}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Upload className="h-4 w-4" />
          )}
          <span className="ml-2">
            {uploading ? "ກຳລັງອັບໂຫຼດ..." : "ອັບໂຫຼດຮູບ"}
          </span>
        </Button>
      </div>
      <p className="text-muted-foreground text-xs">JPEG, PNG, WebP, GIF · ສູງສຸດ 10MB</p>
      {error ? <p className="text-destructive text-sm">{error}</p> : null}
    </div>
  );
}
